import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { updateMembership, type Membership } from '../../lib/api';

/**
 * Recruit rules matrix — one row per (recruiter) tier:
 *   - "No restriction" ON  → `recruit_allowed` is DELETED from the tier's
 *     config (allow all, the default for every existing tenant).
 *   - "No restriction" OFF → the checked columns persist as
 *     `recruit_allowed: string[]` (membership codes; empty = recruit nothing).
 *   - "Binary locked" → `binary_locked: true`; the tier's slots can never
 *     enter the binary tree and never appear in "For Placement".
 * Rows save independently via PATCH /memberships/:id, merging into the tier's
 * existing config so per-plan rates and caps are never clobbered.
 */
export function RecruitRulesCard({ memberships }: { memberships: Membership[] }) {
  const active = memberships.filter((m) => m.active);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recruit rules &amp; binary lock</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Control which packages each tier may recruit, and lock tiers out of the binary tree.
          A binary-locked tier's slots still join the sponsor tree and earn from non-binary
          plans, but never take a binary position (they're hidden from "For Placement").
          Restricting a tier also binds admins and cashiers. Keep the company/root tier
          unrestricted — walk-in signups activate under it.
        </p>
        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active tiers.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3">Tier</th>
                  <th className="py-2 pr-3">No restriction</th>
                  <th className="py-2 pr-3">Can recruit</th>
                  <th className="py-2 pr-3">Binary locked</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {active.map((m) => (
                  <RecruitRuleRow key={m.id} membership={m} allTiers={active} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecruitRuleRow({
  membership,
  allTiers,
}: {
  membership: Membership;
  allTiers: Membership[];
}) {
  const queryClient = useQueryClient();
  const storedAllowed = membership.config?.recruit_allowed;
  const restricted = Array.isArray(storedAllowed);
  const [unrestricted, setUnrestricted] = useState(!restricted);
  const [allowed, setAllowed] = useState<Set<string>>(
    () => new Set(restricted ? (storedAllowed as string[]) : []),
  );
  const [binaryLocked, setBinaryLocked] = useState(membership.config?.binary_locked === true);
  const [dirty, setDirty] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const config = { ...(membership.config ?? {}) } as Record<string, unknown>;
      if (unrestricted) delete config.recruit_allowed;
      else config.recruit_allowed = Array.from(allowed);
      if (binaryLocked) config.binary_locked = true;
      else delete config.binary_locked;
      return updateMembership(membership.id, { config });
    },
    meta: { successMessage: `Recruit rules saved for ${membership.code}.` },
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
    },
  });

  return (
    <tr className="border-b last:border-0 align-top">
      <td className="py-2 pr-3 whitespace-nowrap">
        <span className="font-medium">{membership.name}</span>{' '}
        <span className="text-xs text-muted-foreground font-mono">{membership.code}</span>
      </td>
      <td className="py-2 pr-3">
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={unrestricted}
          onChange={(e) => {
            setUnrestricted(e.target.checked);
            setDirty(true);
          }}
          title="Allow this tier to recruit every package"
        />
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {allTiers.map((t) => (
            <label
              key={t.id}
              className={`flex items-center gap-1.5 text-xs ${unrestricted ? 'opacity-40' : ''}`}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                disabled={unrestricted}
                checked={unrestricted || allowed.has(t.code)}
                onChange={(e) => {
                  setAllowed((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(t.code);
                    else next.delete(t.code);
                    return next;
                  });
                  setDirty(true);
                }}
              />
              {t.code}
            </label>
          ))}
        </div>
      </td>
      <td className="py-2 pr-3">
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={binaryLocked}
          onChange={(e) => {
            setBinaryLocked(e.target.checked);
            setDirty(true);
          }}
          title="Slots of this tier can never enter the binary tree"
        />
      </td>
      <td className="py-2 text-right">
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </td>
    </tr>
  );
}
